// The URL is this app's navigation state.
//
// Everything a shared link has to reproduce — which page, which sub-tab,
// which filters, which sort, which record is open — lives in the path and
// query string. "Send me that screen" is a copy of the address bar, and the
// browser Back button walks the pages the user actually visited.
//
//   Path   /<page>[/<sub>]      /records, /records/12345, /rules/pay_code
//   Query  canonical snake_case keys; empty values are omitted entirely so
//          a default view stays a bare path.
//
// Nothing here touches React — the provider in UrlStateProvider.jsx is the
// only thing that reads/writes history, and it does it through these pure
// helpers.

// Query keys that older links (gap-summary emails, bookmarks) still use, and
// the canonical key each one maps to. Rewritten once at boot so the rest of
// the app only ever reads canonical names.
const LEGACY_PARAM_ALIASES = {
  exclusionReason: 'exclusion_reason',
  siteCode:        'site_code',
  customerOt:      'customer_ot',
  billingTitleId:  'billing_title_id',
  payGroup:        'pay_group',
  dateStart:       'date_start',
  dateEnd:         'date_end',
  search:          'q',
};

// Older paths that moved. `/reference` merged into Rules on 2026-05-07.
const LEGACY_PATHS = {
  reference: 'rules',
};

// Parse `window.location` (or any Location-shaped object) into the two things
// pages care about: the path segments and a plain query object.
export function parseLocation(loc) {
  const l = loc || (typeof window !== 'undefined' ? window.location : null);
  if (!l) return { segments: [], query: {}, depth: 0 };
  const segments = decodeURI(l.pathname || '/').split('/').filter(Boolean);
  const query = {};
  new URLSearchParams(l.search || '').forEach((value, key) => {
    // Last one wins on duplicates — matches URLSearchParams.get().
    query[key] = value;
  });
  const depth = (typeof window !== 'undefined' && window.history.state?.psc_depth) || 0;
  return { segments, query, depth };
}

// Build a canonical href. Null / undefined / '' / false values are dropped:
// that is what keeps a default view at `/records` instead of
// `/records?filter=&q=&site_code=`.
export function buildHref(segments, query) {
  const path = `/${(segments || [])
    .filter((s) => s !== null && s !== undefined && s !== '')
    .map((s) => encodeURIComponent(String(s)))
    .join('/')}`;
  const clean = {};
  for (const [key, value] of Object.entries(query || {})) {
    if (value === null || value === undefined || value === '' || value === false) continue;
    clean[key] = String(value);
  }
  const qs = new URLSearchParams(clean).toString();
  return qs ? `${path}?${qs}` : path;
}

export function currentHref() {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search || ''}`;
}

// Rewrite a legacy URL into the canonical shape, in place, before React reads
// it. Runs once at boot; returns true if anything changed.
//
// Handles three generations of link:
//   /?tab=records&status=gap   — the original query-only deep link
//   /reference                 — the page that merged into Rules
//   ?siteCode=/?exclusionReason= — camelCase params from gap-summary emails
export function canonicalizeUrl(knownPages) {
  if (typeof window === 'undefined') return false;
  const { segments, query } = parseLocation();
  const nextSegments = [...segments];
  const nextQuery = { ...query };
  let changed = false;

  // ?tab=records on the root → /records
  if (nextSegments.length === 0 && nextQuery.tab) {
    const wanted = nextQuery.tab;
    if (!knownPages || knownPages.has(wanted)) {
      nextSegments.push(wanted);
      delete nextQuery.tab;
      changed = true;
    }
  }

  if (nextSegments.length > 0 && LEGACY_PATHS[nextSegments[0]]) {
    nextSegments[0] = LEGACY_PATHS[nextSegments[0]];
    changed = true;
  }

  for (const [legacy, canonical] of Object.entries(LEGACY_PARAM_ALIASES)) {
    if (nextQuery[legacy] === undefined) continue;
    if (nextQuery[canonical] === undefined) nextQuery[canonical] = nextQuery[legacy];
    delete nextQuery[legacy];
    changed = true;
  }

  // Gap-summary emails sent before the Records tabs were named used
  // ?status=gap to mean "open the Gaps tab". `filter` is that key now.
  if (nextQuery.status !== undefined) {
    if (nextQuery.filter === undefined) nextQuery.filter = nextQuery.status;
    delete nextQuery.status;
    changed = true;
  }

  if (!changed) return false;
  window.history.replaceState(
    window.history.state,
    '',
    buildHref(nextSegments, nextQuery),
  );
  return true;
}

// Comma-separated list params (gap types, chosen columns). An empty list is
// stored as '-' rather than omitted, because "no columns selected" and "column
// selection untouched, use the defaults" are different states.
export const EMPTY_LIST = '-';

export function parseList(raw) {
  if (!raw || raw === EMPTY_LIST) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

export function joinList(values) {
  const arr = Array.from(values || []);
  return arr.length === 0 ? EMPTY_LIST : arr.join(',');
}
